import { Element } from "@/components/elements/UIElement";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { Numerals } from "./constants";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const parseElements = (elements: Element[]) => {
  return elements.reduce<Record<string, Element>>((acc, element) => {
    acc[element.id] = element;
    return acc;
  }, {});
};

export const serializeElements = (elements: Record<string, Element>) => {
  return Object.values(elements);
};

export const arrayRemoveAt = <T = unknown>(array: T[], index: number): T[] => {
  const tmp = [...array];
  if (index >= 0 && index < tmp.length) {
    tmp.splice(index, 1);
  }
  return tmp;
};

export const arrayInsertAt = <T = unknown>(
  array: T[],
  index: number,
  item: T,
): T[] => {
  const tmp = [...array];
  if (index >= 0 && index < tmp.length) {
    tmp.splice(index, 0, item);
  }
  return tmp;
};

export const convertNumberToWords = (num: number): string => {
  if (num === 0) return "zero";

  let numStr = "";

  if (num < 10) {
    numStr = Numerals.ones[num];
  } else if (num < 20) {
    numStr = Numerals.teens[num - 10];
  } else {
    if (num >= 100 && num < 1000) {
      const hundreds = Math.floor(num / 100);
      numStr = `${Numerals.ones[hundreds]} hundred`;

      num = num % 100;
      if (num > 0) {
        numStr += " and ";
      }
    }

    const tens = Math.floor(num / 10);
    const ones = num % 10;

    numStr += Numerals.tens[tens];
    if (ones > 0) {
      numStr += `-${Numerals.ones[ones]}`;
    }
  }

  return numStr;
};

export const convertTimeToWords = (time: `${number}:${number}`): string => {
  let words = "";
  const [hours, minutes] = time.split(":").map(Number);
  if (minutes === 0) {
    words = `${convertNumberToWords(hours)} o'clock`;
  } else if (minutes === 15) {
    words = `quarter past ${convertNumberToWords(hours)}`;
  } else if (minutes === 30) {
    words = `half past ${convertNumberToWords(hours)}`;
  } else if (minutes === 45) {
    words = `quarter to ${convertNumberToWords(hours + 1)}`;
  } else if (minutes < 30) {
    words = `${convertNumberToWords(minutes)} past ${convertNumberToWords(hours)}`;
  } else {
    words = `${convertNumberToWords(60 - minutes)} to ${convertNumberToWords(hours + 1)}`;
  }
  return words;
};

export const debounce = <T extends (...args: any[]) => void>(
  fn: T,
  delay: number,
) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fn(...args);
      timeoutId = null;
    }, delay);
  };
};
